import { addDays, DateOnly } from '@if/core/date'
import { t } from '@if/core/i18n'
import { formatCurrencyNoDecimals } from '@if/core/utils'
import * as yup from '@if/core/yup'
import {
  ControlledDateOnlyPicker,
  ControlledNumberField,
  ControlledPercentField,
  ControlledPercentSlider,
  ErrorAlert,
  Help,
  StatCard,
  useDebouncedWatch,
  useFormDialog,
} from '@if/mui'
import { Box, Card, CardContent, CardHeader, Divider, Grid, Skeleton, Stack, Typography } from '@mui/material'
import ApprovalLikelihoodBar, { ApprovalLikelihood } from '@src/components/ApprovalLikelihoodBar'
import {
  PromoPlanContentDto,
  PromoPlanDto,
  useGetCustomBuydownTermRates,
  useUpsertCustomBuydown,
} from '@src/data/api/merchants-api/merchants-api'
import { applyRebateToTermRates } from '@src/data/customBuydownRates'
import { CUSTOM_BUYDOWN_FAMILY } from '@src/data/types/Constants'
import { useMemo } from 'react'
import { UseFormReturn, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

const ADMIN_FEE_RATE = 0.06
const MERCHANT_FEE_RATE_MIN = 0.005
const MERCHANT_FEE_RATE_MAX = 0.2
// Max financing amount cannot go below the smallest issuable loan.
const MIN_LOAN_AMOUNT = 500

const contributionMarks = [0, 5, 10, 15, 20].map((value) => ({ value, label: `${value}%` }))

// Skeletons match a StatCard's size so the dialog height stays stable while rates load.
const TERM_CARD_HEIGHT = 88
const TERM_SKELETON_COUNT = 5

const formatPercent = (value: number): string => `${(value * 100).toFixed(2)}%`
const buildPlanCode = (merchantFeeRate: number): string =>
  `FLASH${Math.round(merchantFeeRate * 10000)}/${ADMIN_FEE_RATE * 100}`

const getApprovalLikelihood = (merchantFeeRate: number): ApprovalLikelihood => {
  // TODO CHANGE FOR THE REAL ONE (this is a mocked calculator)
  if (merchantFeeRate < 0.05) return 'low'
  if (merchantFeeRate < 0.08) return 'moderate'
  if (merchantFeeRate < 0.12) return 'good'
  return 'high'
}

const getCustomBuydownFormSchema = () =>
  yup.object().shape({
    merchantFeeRate: yup
      .number()
      .min(MERCHANT_FEE_RATE_MIN, () => t('merchant.customBuydown.errors.merchantFeeRateMin', { min: 0.5 }))
      .max(MERCHANT_FEE_RATE_MAX, () => t('merchant.customBuydown.errors.merchantFeeRateMax', { max: 20 }))
      .required(),
    // Null means no maximum (unlimited).
    maxFinancingAmount: yup
      .number()
      .min(MIN_LOAN_AMOUNT, () => t('merchant.customBuydown.errors.maxFinancingAmountMin', { min: MIN_LOAN_AMOUNT }))
      .defined()
      .nullable(),
    activeFrom: yup.dateOnly().min(addDays(DateOnly.today(), 1)).required(),
    expiresOn: yup.dateOnly().defined().nullable().min(yup.ref('activeFrom'), { offsetDays: 1 }),
  })

export type CustomBuydownFormValues = yup.InferType<ReturnType<typeof getCustomBuydownFormSchema>>

const getDefaults = (plan: PromoPlanDto | null): CustomBuydownFormValues => {
  if (!plan) {
    return {
      merchantFeeRate: MERCHANT_FEE_RATE_MIN,
      maxFinancingAmount: null,
      activeFrom: addDays(DateOnly.today(), 1),
      expiresOn: null,
    }
  }

  const planActiveFrom = new DateOnly(plan.activeFrom)
  const earliestActiveFrom = addDays(DateOnly.today(), 1)

  return {
    merchantFeeRate: plan.merchantFeeRate,
    // Plan-level on the form; the backend stamps it onto every computed term rate.
    maxFinancingAmount: plan.promoTermRates[0]?.maxFinancingAmount ?? null,
    // Clamp a past activeFrom up to tomorrow so the default satisfies the schema's min.
    activeFrom: planActiveFrom > earliestActiveFrom ? planActiveFrom : earliestActiveFrom,
    expiresOn: plan.expiresOn ? new DateOnly(plan.expiresOn) : null,
  }
}

const toContentDto = (values: CustomBuydownFormValues): PromoPlanContentDto => ({
  family: CUSTOM_BUYDOWN_FAMILY,
  label: buildPlanCode(values.merchantFeeRate),
  promoCodePrefix: buildPlanCode(values.merchantFeeRate),
  merchantFeeRate: values.merchantFeeRate,
  borrowerFeeRate: ADMIN_FEE_RATE,
  maxFinancingAmount: values.maxFinancingAmount ?? null,
  activeFrom: values.activeFrom.toString(),
  expiresOn: values.expiresOn?.toString() ?? null,
  supportsChargeback: false,
})

/**
 * Live per-term rate grid from the same backend calc the Save path freezes onto the plan.
 * The backend owns the term list; terms it filtered out (e.g. by the max-amount cap) are absent.
 */
// `rebate` is the merchant-level rebate (live value from the promotion form), applied to the
// previewed rates for display only — it is never frozen onto the plan.
const RatePreview = ({
  merchantId,
  form,
  rebate,
}: {
  merchantId: string
  form: UseFormReturn<CustomBuydownFormValues>
  rebate: number
}) => {
  const { t } = useTranslation()

  const debouncedFeeRate = useDebouncedWatch({ control: form.control, name: 'merchantFeeRate' })
  const debouncedMaxAmount = useDebouncedWatch({ control: form.control, name: 'maxFinancingAmount' })
  const { isValid } = form.formState
  const canPreview =
    isValid &&
    debouncedFeeRate >= MERCHANT_FEE_RATE_MIN &&
    debouncedFeeRate <= MERCHANT_FEE_RATE_MAX &&
    (debouncedMaxAmount === null || debouncedMaxAmount >= 0)
  const [preview, isLoading, error] = useGetCustomBuydownTermRates(
    merchantId,
    debouncedFeeRate,
    ADMIN_FEE_RATE,
    debouncedMaxAmount,
    canPreview,
  )

  // The endpoint returns base rates; the merchant rebate is subtracted client-side for display.
  const previewRates = preview && applyRebateToTermRates(preview, rebate)

  // Show skeletons (not a text hint) until rates load, to avoid shifting the dialog height.
  const showSkeletons = !canPreview || (isLoading && !preview)

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        {t('merchant.customBuydown.availableTerms')}
      </Typography>
      {canPreview && error ? (
        <ErrorAlert error={error} disabledGutterBottom />
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 1.5 }}>
          {showSkeletons
            ? Array.from({ length: TERM_SKELETON_COUNT }).map((_, index) => (
                <Skeleton key={index} variant="rounded" height={TERM_CARD_HEIGHT} />
              ))
            : previewRates?.map((rate) => (
                <StatCard
                  key={rate.termMonths}
                  variant="outlined"
                  label={`${rate.termMonths} ${t('common.months')}`}
                  value={formatPercent(rate.promoRate)}
                />
              ))}
        </Box>
      )}
    </Box>
  )
}

const SummaryFact = ({ label, value, caption }: { label: string; value: string; caption?: string }) => (
  <Box>
    <Typography variant="overline" color="primary" lineHeight={1.5} component="div">
      {label}
    </Typography>
    <Typography variant="h6" fontWeight="bold" component="div">
      {value}
    </Typography>
    {caption && (
      <Typography variant="caption" color="text.secondary" component="div">
        {caption}
      </Typography>
    )}
  </Box>
)

// Plan code, estimated merchant cost, and approval-likelihood signal. Pure client-side math,
// so it reacts to the contribution as typed (no debounce, unlike RatePreview's backend fetch).
const PlanSummary = ({ form }: { form: UseFormReturn<CustomBuydownFormValues> }) => {
  const { t } = useTranslation()

  const { isValid } = form.formState
  const merchantFeeRate = useWatch({ control: form.control, name: 'merchantFeeRate' })
  const maxFinancingAmount = useWatch({ control: form.control, name: 'maxFinancingAmount' })
  const likelihood = isValid ? getApprovalLikelihood(merchantFeeRate) : null

  return (
    <Card variant="outlined">
      <CardHeader
        title={t('merchant.customBuydown.planSummary')}
        slotProps={{ title: { variant: 'subtitle1', color: 'primary' } }}
      />
      <CardContent sx={{ pt: 0 }}>
        <Stack gap={2} divider={<Divider />}>
          <SummaryFact
            label={t('merchant.customBuydown.planCode')}
            value={isValid ? buildPlanCode(merchantFeeRate) : '—'}
          />
          {maxFinancingAmount != null && (
            <SummaryFact
              label={t('merchant.customBuydown.estimatedCost')}
              value={isValid ? formatCurrencyNoDecimals(merchantFeeRate * maxFinancingAmount) : '—'}
              caption={t('merchant.customBuydown.estimatedCostCaption', {
                amount: formatCurrencyNoDecimals(maxFinancingAmount),
              })}
            />
          )}
          {likelihood && (
            <ApprovalLikelihoodBar
              label={t('merchant.customBuydown.customerApproval')}
              likelihood={likelihood}
              hint={likelihood !== 'high' ? t('merchant.customBuydown.customerApprovalHint') : undefined}
            />
          )}
        </Stack>
      </CardContent>
    </Card>
  )
}

/**
 * Edit/create dialog for a merchant's single Custom Buydown. Per-term rates are computed
 * server-side, the admin fee is locked, and the plan code is derived from the contribution.
 * Save upserts via the PromoPlan endpoints (POST to mint, PUT to append a revision).
 *
 * `rebate` is the merchant-level rate rebate (live value from the promotion form). It is applied to
 * the previewed customer rates for display only; the dialog never edits or saves it.
 */
export function useCustomBuydownDialog(merchantId: string, rebate: number) {
  const { t } = useTranslation()
  const [upsertCustomBuydown] = useUpsertCustomBuydown()
  const schema = useMemo(() => getCustomBuydownFormSchema(), [])

  return useFormDialog<CustomBuydownFormValues, PromoPlanDto | null, PromoPlanDto>({
    title: {
      create: t('merchant.customBuydown.createTitle'),
      edit: t('merchant.customBuydown.editTitle'),
    },
    fullWidth: true,
    maxWidth: 'lg',
    defaultValues: (plan) => getDefaults(plan),
    mode: 'onChange',
    schema,
    successMessage: (saved) => t('merchant.customBuydown.saved', { planCode: saved.promoCodePrefix || saved.label }),
    content: ({ form }) => (
      <Grid container columns={12} spacing={2}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Stack gap={2} divider={<Divider />}>
            <Grid container columns={12} spacing={2}>
              <Grid size={{ xs: 12, md: 4 }}>
                <ControlledDateOnlyPicker
                  name="activeFrom"
                  control={form.control}
                  label={t('merchant.promo.activeFrom')}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <ControlledDateOnlyPicker
                  name="expiresOn"
                  control={form.control}
                  label={t('merchant.promo.expiresOn')}
                  clearable
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <ControlledNumberField
                  name="maxFinancingAmount"
                  control={form.control}
                  label={t('merchant.customBuydown.maxFinancingAmount')}
                  fullWidth
                  isMoney
                  disableDecimals
                />
              </Grid>
            </Grid>
            <Card variant="outlined">
              <CardHeader
                title={t('merchant.customBuydown.contributionTitle')}
                subheader={
                  <Stack direction="row" alignItems="center" gap={0.5}>
                    <span>{t('merchant.customBuydown.contributionSubtitle')}</span>
                    <Help title={t('merchant.customBuydown.contributionHelp')} />
                  </Stack>
                }
                action={
                  <ControlledPercentField
                    name="merchantFeeRate"
                    control={form.control}
                    label={t('merchant.customBuydown.contribution')}
                    size="small"
                    sx={{ width: 140 }}
                  />
                }
                slotProps={{ action: { sx: { alignSelf: 'center', m: 0 } } }}
              />
              <CardContent sx={{ pt: 0 }}>
                <Box sx={{ px: 1 }}>
                  <ControlledPercentSlider
                    name="merchantFeeRate"
                    control={form.control}
                    min={0.5}
                    max={20}
                    step={0.01}
                    marks={contributionMarks}
                  />
                </Box>
              </CardContent>
            </Card>
            <RatePreview merchantId={merchantId} form={form} rebate={rebate} />
            <Box>
              <Stack direction="row" alignItems="center" gap={0.5}>
                <Typography>{t('merchant.customBuydown.adminFee')}</Typography>
                <Help title={t('merchant.customBuydown.adminFeeTooltip')} />
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {t('merchant.customBuydown.adminFeeLocked')}
              </Typography>
            </Box>
          </Stack>
        </Grid>
        {/* Sticky so the summary stays visible while the form scrolls. */}
        <Grid size={{ xs: 12, md: 4 }} sx={{ position: 'sticky', top: 0, alignSelf: 'flex-start' }}>
          <PlanSummary form={form} />
        </Grid>
      </Grid>
    ),
    onSubmit: async (values, { props }) =>
      upsertCustomBuydown({ merchantId, planKey: props?.planKey ?? null, content: toContentDto(values) }),
  })
}
